import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MAX_TOTAL_PLOTS, usePlotMapGeneration } from "@/hooks/usePlotMapGeneration";
import { cn } from "@/lib/utils";
import { CSVValidationResult, validateCSVShapeDetailed } from "@/utils/csvUtils";
import { ExcelSheetInfo, convertExcelSheetToCSV, createCSVFileFromContent, isExcelFile, readExcelFile } from "@/utils/excelUtils";
import { AlertTriangle, CheckCircle, FileText, HelpCircle, Info, Upload } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PlotMapHelpDialog from "./PlotMapHelpDialog";

// Maximum plots allowed for custom naming
const MAX_PLOTS_FOR_CUSTOM_NAMES = 500;

// Type for plot map data output
export type PlotMapData = {
  type: "upload";
  file: File;
  originalFile?: File;
  selectedSheet?: string;
} | {
  type: "generate";
  rows: number;
  columns: number;
} | {
  type: "sequence";
  count: number;
  customNames?: string[];
};

// Initial values for edit mode
export interface PlotMapInitialValues {
  plotMapSource: "upload" | "generate" | "sequence";
  // For uploaded files
  plotIdColumn?: string;
  rowColumn?: string;
  colColumn?: string;
  csvHeaders?: string[];
  originalFileName?: string;
  // For generated grids
  numRows?: number;
  numCols?: number;
  // For sequence generation
  count?: number;
  customNames?: string[];
}

// Change data emitted to parent
export interface PlotMapChangeData {
  type: "upload" | "generate" | "sequence" | null;
  isValid: boolean;
  hasChanges: boolean;
  // Upload data
  file?: File;
  originalFile?: File;
  selectedSheet?: string;
  csvValidationResult?: CSVValidationResult;
  plotIdColumn?: string;
  rowColumn?: string;
  colColumn?: string;
  // Generate data
  rows?: number;
  columns?: number;
  // Sequence data
  count?: number;
  customNames?: string[];
}

interface PlotMapSectionProps {
  // Mode configuration
  mode: "create" | "edit";
  disabled?: boolean;
  disabledMessage?: string;

  // Initial values for edit mode
  initialValues?: PlotMapInitialValues;

  // Current plot count (for edit mode display)
  currentPlotCount?: number;

  // Callbacks
  onChange?: (data: PlotMapChangeData) => void;

  // Validation state (controlled from parent)
  showFieldError?: boolean;
}

interface CustomPlotNamesSectionProps {
  count: number;
  names: string[];
  onNameChange: (index: number, name: string) => void;
  duplicateIndices: number[];
  disabled?: boolean;
}

const CustomPlotNamesSection: React.FC<CustomPlotNamesSectionProps> = ({
  count, names, onNameChange, duplicateIndices, disabled
}) => {
  const filledCount = names.filter(n => n?.trim()).length;
  const showWarning = count > 100;

  return (
    <div className="space-y-3 border-t pt-3">
      {showWarning && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Custom naming is enabled for {count.toLocaleString()} plots. This may take some time to fill in.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium">
          Plot Names ({filledCount}/{count} provided)
        </Label>

        <div
          className="max-h-96 overflow-y-auto border rounded-md p-4 bg-white"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: count }, (_, i) => {
              const isDuplicate = duplicateIndices.includes(i);
              const hasValue = names[i]?.trim();

              return (
                <div key={i} className="space-y-1">
                  <Label
                    htmlFor={`plot-name-${i}`}
                    className={cn(
                      "text-xs",
                      isDuplicate && "text-destructive"
                    )}
                  >
                    Plot {i + 1}
                  </Label>
                  <Input
                    id={`plot-name-${i}`}
                    value={names[i] || ""}
                    onChange={e => onNameChange(i, e.target.value)}
                    placeholder={`Plot ${i + 1}`}
                    className={cn(
                      "text-sm",
                      isDuplicate && "border-destructive",
                      !hasValue && "border-yellow-500"
                    )}
                    disabled={disabled}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Validation messages */}
        {duplicateIndices.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Duplicate plot names detected. Each plot must have a unique name.
            </AlertDescription>
          </Alert>
        )}

        {filledCount < count && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              All {count} plot names must be filled in before submitting.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

const PlotMapSection: React.FC<PlotMapSectionProps> = ({
  mode,
  disabled = false,
  disabledMessage,
  initialValues,
  currentPlotCount,
  onChange,
  showFieldError = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);

  // Track if we've initialized from initial values
  const [hasInitialized, setHasInitialized] = useState(false);

  // Plot map generation hook
  const {
    plotRows,
    plotColumns,
    plotCount,
    customizePlotNames,
    customPlotNames,
    plotMapSource,
    setPlotRows,
    setPlotColumns,
    setPlotCount,
    setCustomizePlotNames,
    setCustomPlotNames,
    setPlotMapSource,
    generationData,
    isValidGeneration,
    isValidSequence,
    exceedsMaxPlots,
    duplicateNameIndices,
  } = usePlotMapGeneration();

  // CSV validation and column selection state
  const [csvValidationResult, setCsvValidationResult] = useState<CSVValidationResult | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvWarnings, setCsvWarnings] = useState<string[]>([]);

  // Excel processing state
  const [isExcelFileState, setIsExcelFileState] = useState(false);
  const [availableSheets, setAvailableSheets] = useState<ExcelSheetInfo[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [originalExcelFile, setOriginalExcelFile] = useState<File | null>(null);
  const [plotIdColumn, setPlotIdColumn] = useState<string>("");
  const [rowColumn, setRowColumn] = useState<string>("");
  const [colColumn, setColColumn] = useState<string>("");
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Compute valid row options based on rowColumnCandidates
  // A column is a valid row option if it has at least one valid column partner
  // OR if a column is already selected, only show rows that pair with that column
  const validRowOptions = useMemo(() => {
    const candidates = csvValidationResult?.columnAnalysis?.rowColumnCandidates || {};
    const integerColumns = csvValidationResult?.columnAnalysis?.integerColumns || [];

    if (Object.keys(candidates).length === 0) {
      // No valid pairs exist - return empty array
      return [];
    }

    if (colColumn) {
      // Filter to only rows that have the selected column as a valid partner
      return Object.keys(candidates).filter(
        row => candidates[row]?.includes(colColumn) && row !== plotIdColumn
      );
    }

    // No column selected - show all rows that have at least one valid partner
    return Object.keys(candidates).filter(row => row !== plotIdColumn);
  }, [csvValidationResult?.columnAnalysis, colColumn, plotIdColumn]);

  // Compute valid column options based on rowColumnCandidates
  // If a row is selected, only show columns that pair uniquely with that row
  const validColOptions = useMemo(() => {
    const candidates = csvValidationResult?.columnAnalysis?.rowColumnCandidates || {};

    if (Object.keys(candidates).length === 0) {
      // No valid pairs exist - return empty array
      return [];
    }

    if (rowColumn) {
      // Show columns that are valid partners for the selected row
      return (candidates[rowColumn] || []).filter(col => col !== plotIdColumn);
    }

    // No row selected - show all unique column options from all candidates
    const allColumns = new Set<string>();
    Object.values(candidates).forEach(cols => {
      cols.forEach(col => {
        if (col !== plotIdColumn) {
          allColumns.add(col);
        }
      });
    });
    return Array.from(allColumns);
  }, [csvValidationResult?.columnAnalysis, rowColumn, plotIdColumn]);

  // Check if there are no valid row/column combinations available
  const noValidRowColumnPairs = useMemo(() => {
    const candidates = csvValidationResult?.columnAnalysis?.rowColumnCandidates || {};
    const integerColumns = csvValidationResult?.columnAnalysis?.integerColumns || [];
    return integerColumns.length > 0 && Object.keys(candidates).length === 0;
  }, [csvValidationResult?.columnAnalysis]);

  // Check if there are no numeric columns at all for grid layout
  const noNumericColumnsForGrid = useMemo(() => {
    const integerColumns = csvValidationResult?.columnAnalysis?.integerColumns || [];
    return integerColumns.length === 0;
  }, [csvValidationResult?.columnAnalysis]);

  // Clear invalid selections when options change
  useEffect(() => {
    if (rowColumn && !validRowOptions.includes(rowColumn)) {
      setRowColumn("");
    }
  }, [validRowOptions, rowColumn]);

  useEffect(() => {
    if (colColumn && !validColOptions.includes(colColumn)) {
      setColColumn("");
    }
  }, [validColOptions, colColumn]);

  // Auto-fill row/column when there's only one valid combination
  useEffect(() => {
    // Only auto-fill if both are currently empty and we have exactly one valid pair
    if (rowColumn || colColumn) return;

    const candidates = csvValidationResult?.columnAnalysis?.rowColumnCandidates || {};
    const rowKeys = Object.keys(candidates);

    // Check if there's exactly one row option
    if (rowKeys.length === 1) {
      const onlyRow = rowKeys[0];
      const colOptions = candidates[onlyRow] || [];

      // Check if there's exactly one column option for that row
      if (colOptions.length === 1) {
        const onlyCol = colOptions[0];
        // Make sure neither conflicts with plotIdColumn
        if (onlyRow !== plotIdColumn && onlyCol !== plotIdColumn) {
          setRowColumn(onlyRow);
          setColColumn(onlyCol);
          setHasUserMadeChanges(true);
        }
      }
    }
  }, [csvValidationResult?.columnAnalysis, rowColumn, colColumn, plotIdColumn]);

  // Initialize from initial values in edit mode
  useEffect(() => {
    if (mode === "edit" && initialValues && !hasInitialized) {
      setPlotMapSource(initialValues.plotMapSource);

      if (initialValues.plotMapSource === "generate") {
        if (initialValues.numRows) setPlotRows(String(initialValues.numRows));
        if (initialValues.numCols) setPlotColumns(String(initialValues.numCols));
      } else if (initialValues.plotMapSource === "upload") {
        // For upload mode, we can show the original column selections but can't restore the file
        if (initialValues.plotIdColumn) setPlotIdColumn(initialValues.plotIdColumn);
        if (initialValues.rowColumn) setRowColumn(initialValues.rowColumn);
        if (initialValues.colColumn) setColColumn(initialValues.colColumn);
      }

      setHasInitialized(true);
    }
  }, [mode, initialValues, hasInitialized, setPlotMapSource, setPlotRows, setPlotColumns]);

  // Notify parent of changes
  useEffect(() => {
    if (!onChange) return;

    const isValid = plotMapSource === "upload"
      ? (selectedFile !== null && !csvError && csvValidationResult?.isValid && plotIdColumn !== "")
      : plotMapSource === "generate"
      ? isValidGeneration
      : isValidSequence;

    const changeData: PlotMapChangeData = {
      type: plotMapSource,
      isValid,
      hasChanges: hasUserMadeChanges,
    };

    if (plotMapSource === "upload" && selectedFile) {
      changeData.file = selectedFile;
      changeData.originalFile = originalExcelFile || undefined;
      changeData.selectedSheet = selectedSheet || undefined;
      changeData.csvValidationResult = csvValidationResult || undefined;
      changeData.plotIdColumn = plotIdColumn || undefined;
      changeData.rowColumn = rowColumn || undefined;
      changeData.colColumn = colColumn || undefined;
    } else if (plotMapSource === "generate" && generationData) {
      changeData.rows = generationData.rows;
      changeData.columns = generationData.columns;
    } else if (plotMapSource === "sequence" && plotCount) {
      const count = parseInt(plotCount);
      if (!isNaN(count) && count > 0) {
        changeData.count = count;
        if (customizePlotNames && customPlotNames.length > 0) {
          changeData.customNames = customPlotNames;
        }
      }
    }

    onChange(changeData);
  }, [
    onChange,
    plotMapSource,
    selectedFile,
    csvError,
    csvValidationResult,
    plotIdColumn,
    rowColumn,
    colColumn,
    isValidGeneration,
    isValidSequence,
    generationData,
    plotCount,
    customizePlotNames,
    customPlotNames,
    originalExcelFile,
    selectedSheet,
    hasUserMadeChanges,
  ]);

  const convertExcelSheetToCSVAndProcess = useCallback(async (file: File, sheetName: string) => {
    try {
      setIsProcessingExcel(true);
      const result = await convertExcelSheetToCSV(file, sheetName);
      if (!result.isValid) {
        setCsvError(result.error || "Failed to convert Excel sheet to CSV");
        return;
      }

      const csvFile = createCSVFileFromContent(result.csvContent!, file.name, sheetName);
      setSelectedFile(csvFile);
      await handleFileUpload(csvFile);
    } catch (error) {
      setCsvError("Failed to convert Excel sheet to CSV. Please try again.");
      console.error("Excel to CSV conversion error:", error);
    } finally {
      setIsProcessingExcel(false);
    }
  }, []);

  // Handle sheet selection for Excel files
  useEffect(() => {
    const processSelectedSheet = async () => {
      if (selectedSheet && originalExcelFile && isExcelFileState && availableSheets.length > 1) {
        await convertExcelSheetToCSVAndProcess(originalExcelFile, selectedSheet);
      }
    };
    processSelectedSheet();
  }, [selectedSheet, originalExcelFile, isExcelFileState, availableSheets.length, convertExcelSheetToCSVAndProcess]);

  const resetFileProcessingState = () => {
    setCsvError(null);
    setCsvWarnings([]);
    setCsvValidationResult(null);
    setPlotIdColumn("");
    setRowColumn("");
    setColColumn("");
    setIsExcelFileState(false);
    setAvailableSheets([]);
    setSelectedSheet("");
    setIsProcessingExcel(false);
    setOriginalExcelFile(null);
  };

  const handleExcelFileUpload = async (file: File) => {
    try {
      setIsProcessingExcel(true);
      setCsvError(null);
      setCsvWarnings([]);
      const result = await readExcelFile(file);
      if (!result.isValid) {
        setCsvError(result.error || "Invalid Excel file");
        return;
      }
      if (result.warnings) {
        setCsvWarnings(result.warnings);
      }
      setAvailableSheets(result.sheets || []);

      if (result.sheets && result.sheets.length === 1) {
        const sheetName = result.sheets[0].name;
        setSelectedSheet(sheetName);
        await convertExcelSheetToCSVAndProcess(file, sheetName);
      }
    } catch (error) {
      setCsvError("Failed to process Excel file. Please try again.");
      console.error("Excel processing error:", error);
    } finally {
      setIsProcessingExcel(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      resetFileProcessingState();
      setHasUserMadeChanges(true);

      const isExcel = isExcelFile(file);
      setIsExcelFileState(isExcel);
      if (isExcel) {
        setOriginalExcelFile(file);
        await handleExcelFileUpload(file);
      } else {
        await handleFileUpload(file);
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setCsvError(null);
      setCsvWarnings([]);
      setCsvValidationResult(null);
      setPlotIdColumn("");
      setRowColumn("");
      setColColumn("");
      const result = await validateCSVShapeDetailed(file);
      if (!result.isValid) {
        setCsvError(result.error || "Invalid CSV file");
        return;
      }
      if (result.warnings) {
        setCsvWarnings(result.warnings);
      }
      setCsvValidationResult(result);

      if (result.columnAnalysis?.uniqueValueColumns && result.columnAnalysis.uniqueValueColumns.length > 0) {
        const firstUniqueColumn = result.columnAnalysis.uniqueValueColumns[0];
        setPlotIdColumn(firstUniqueColumn);
      }
    } catch (error) {
      setCsvError("Failed to process CSV file. Please try again.");
      console.error("CSV processing error:", error);
    }
  };

  const handlePlotMapUpload = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleSourceChange = (source: "upload" | "generate" | "sequence") => {
    if (disabled) return;
    setPlotMapSource(source);
    setHasUserMadeChanges(true);
  };

  const handleRowsChange = (value: string) => {
    if (disabled) return;
    setPlotRows(value);
    setHasUserMadeChanges(true);
  };

  const handleColumnsChange = (value: string) => {
    if (disabled) return;
    setPlotColumns(value);
    setHasUserMadeChanges(true);
  };

  const handlePlotCountChange = (value: string) => {
    if (disabled) return;
    const count = parseInt(value);
    setPlotCount(value);
    setHasUserMadeChanges(true);

    // If count exceeds limit, automatically uncheck customizePlotNames
    if (!isNaN(count) && count > MAX_PLOTS_FOR_CUSTOM_NAMES && customizePlotNames) {
      setCustomizePlotNames(false);
      setCustomPlotNames([]);
    }
    // Initialize or resize customPlotNames array when count changes
    else if (customizePlotNames && !isNaN(count) && count > 0) {
      const newNames = Array.from({ length: count }, (_, i) => customPlotNames[i] || "");
      setCustomPlotNames(newNames);
    }
  };

  const handlePlotIdColumnChange = (value: string) => {
    if (disabled) return;
    setPlotIdColumn(value);
    setHasUserMadeChanges(true);
  };

  const handleRowColumnChange = (value: string) => {
    if (disabled) return;
    setRowColumn(value === "none" ? "" : value);
    setHasUserMadeChanges(true);
  };

  const handleColColumnChange = (value: string) => {
    if (disabled) return;
    setColColumn(value === "none" ? "" : value);
    setHasUserMadeChanges(true);
  };

  const handleSheetChange = (value: string) => {
    if (disabled) return;
    setSelectedSheet(value);
    setHasUserMadeChanges(true);
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-4">
        <Label className={showFieldError ? "text-destructive" : ""}>
          Plot Map{mode === "edit" && currentPlotCount !== undefined ? ` (${currentPlotCount} plots)` : ""} {mode === "create" && <span className={showFieldError ? "text-destructive" : "text-muted-foreground"}>(required)</span>}
        </Label>

        {/* Disabled message for edit mode with existing data */}
        {disabled && disabledMessage && (
          <Alert className="bg-muted">
            <Info className="h-4 w-4" />
            <AlertDescription>
              {disabledMessage}
            </AlertDescription>
          </Alert>
        )}


        {/* Plot Map Source Selection */}
        <div className={cn("flex flex-wrap gap-x-6 gap-y-2", disabled && "opacity-50")}>
          <div className="flex items-center space-x-2">
            <input
              id="uploadPlotMap"
              type="radio"
              name="plotMapSource"
              value="upload"
              checked={plotMapSource === "upload"}
              onChange={e => handleSourceChange(e.target.value as "upload" | "generate" | "sequence")}
              className="text-primary"
              disabled={disabled}
            />
            <Label htmlFor="uploadPlotMap" className={cn("text-sm font-normal", disabled && "cursor-not-allowed")}>
              {mode === "edit" ? "Upload New Excel or CSV" : "Upload Excel or CSV"}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setHelpDialogOpen(true);
                      }}
                      className="ml-1 align-super"
                    >
                      <HelpCircle className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Click for formatting instructions and examples</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id="generatePlotMap"
              type="radio"
              name="plotMapSource"
              value="generate"
              checked={plotMapSource === "generate"}
              onChange={e => handleSourceChange(e.target.value as "upload" | "generate" | "sequence")}
              className="text-primary"
              disabled={disabled}
            />
            <Label htmlFor="generatePlotMap" className={cn("text-sm font-normal", disabled && "cursor-not-allowed")}>
              {mode === "edit" ? "Generate New Plot Map (as Grid)" : "Create Plot Map (as Grid)"}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id="sequencePlotMap"
              type="radio"
              name="plotMapSource"
              value="sequence"
              checked={plotMapSource === "sequence"}
              onChange={e => handleSourceChange(e.target.value as "upload" | "generate" | "sequence")}
              className="text-primary"
              disabled={disabled}
            />
            <Label htmlFor="sequencePlotMap" className={cn("text-sm font-normal", disabled && "cursor-not-allowed")}>
              {mode === "edit" ? "Generate New Plot Map (no Grid)" : "Create Plot Map (no Grid)"}
            </Label>
          </div>
        </div>

        {/* Upload Section */}
        {plotMapSource === "upload" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.xlsm"
              onChange={handleFileChange}
              className="hidden"
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              className={cn(
                "w-full border-dashed border-gray-300 py-2 flex flex-col items-center justify-center gap-2 h-auto",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              onClick={handlePlotMapUpload}
              disabled={disabled}
            >
              <Upload className="h-6 w-6 text-gray-400" />
              <div className="text-sm text-gray-600">
                {selectedFile ? (
                  isExcelFileState && originalExcelFile ?
                    `Selected: ${originalExcelFile.name} (Sheet: ${selectedSheet || "Not selected"})` :
                    `Selected: ${selectedFile.name}`
                ) : mode === "edit" && initialValues?.originalFileName ? (
                  `Original: ${initialValues.originalFileName} - Click to upload new file`
                ) : (
                  "Click to upload a CSV or Excel plot map file"
                )}
              </div>
            </Button>
          </>
        )}

        {/* Generate Section */}
        {plotMapSource === "generate" && (
          <div className={cn(
            "grid grid-cols-2 gap-4 p-4 border rounded-lg",
            exceedsMaxPlots ? "border-red-500 bg-red-50" : "bg-muted/50",
            disabled && "opacity-50"
          )}>
            <div className="space-y-2">
              <Label htmlFor="plotRows" className={cn("text-sm", exceedsMaxPlots && "text-red-600")}>
                Number of Rows*
              </Label>
              <Input
                id="plotRows"
                type="number"
                min="1"
                value={plotRows}
                onChange={e => handleRowsChange(e.target.value)}
                placeholder="e.g., 10"
                className={cn(exceedsMaxPlots && "border-red-500 focus-visible:ring-red-500")}
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plotColumns" className={cn("text-sm", exceedsMaxPlots && "text-red-600")}>
                Number of Columns*
              </Label>
              <Input
                id="plotColumns"
                type="number"
                min="1"
                value={plotColumns}
                onChange={e => handleColumnsChange(e.target.value)}
                placeholder="e.g., 20"
                className={cn(exceedsMaxPlots && "border-red-500 focus-visible:ring-red-500")}
                disabled={disabled}
              />
            </div>
            {plotRows && plotColumns && (
              exceedsMaxPlots ? (
                <div className="col-span-2 text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {(parseInt(plotRows) * parseInt(plotColumns)).toLocaleString()} plots exceeds the maximum of {MAX_TOTAL_PLOTS.toLocaleString()} plots
                </div>
              ) : (
                <div className="col-span-2 text-sm text-muted-foreground">
                  This will generate {(parseInt(plotRows) * parseInt(plotColumns)).toLocaleString()} plots in a {plotRows}×{plotColumns} grid
                </div>
              )
            )}
          </div>
        )}

        {/* Sequence Section */}
        {plotMapSource === "sequence" && (
          <div className={cn(
            "space-y-4 border rounded-lg p-4",
            exceedsMaxPlots ? "border-red-500 bg-red-50" : "bg-muted/50",
            disabled && "opacity-50"
          )}>
            {/* Number of Plots Input */}
            <div className="space-y-2">
              <Label htmlFor="plotCount">Number of Plots*</Label>
              <Input
                id="plotCount"
                type="number"
                min="1"
                value={plotCount}
                onChange={e => handlePlotCountChange(e.target.value)}
                placeholder="e.g., 50"
                disabled={disabled}
              />
            </div>

            {/* Plot count summary */}
            {plotCount && (
              exceedsMaxPlots ? (
                <div className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {parseInt(plotCount).toLocaleString()} plots exceeds maximum of {MAX_TOTAL_PLOTS.toLocaleString()}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  This will generate {parseInt(plotCount).toLocaleString()} plots
                </div>
              )
            )}

            {/* Customize names checkbox */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="customizePlotNames"
                      checked={customizePlotNames}
                      onChange={e => {
                        const checked = e.target.checked;
                        setCustomizePlotNames(checked);
                        if (checked && plotCount) {
                          const count = parseInt(plotCount);
                          if (!isNaN(count) && count > 0) {
                            setCustomPlotNames(Array.from({ length: count }, () => ""));
                          }
                        }
                        setHasUserMadeChanges(true);
                      }}
                      disabled={
                        disabled ||
                        !plotCount ||
                        (plotCount && parseInt(plotCount) > MAX_PLOTS_FOR_CUSTOM_NAMES)
                      }
                      className="text-primary"
                    />
                    <Label
                      htmlFor="customizePlotNames"
                      className={cn(
                        "text-sm font-normal",
                        (disabled ||
                          !plotCount ||
                          (plotCount && parseInt(plotCount) > MAX_PLOTS_FOR_CUSTOM_NAMES)) &&
                          "cursor-not-allowed opacity-50"
                      )}
                    >
                      Customize plot names
                    </Label>
                  </div>
                </TooltipTrigger>
                {plotCount && parseInt(plotCount) > MAX_PLOTS_FOR_CUSTOM_NAMES && (
                  <TooltipContent>
                    <p className="max-w-xs">
                      Custom plot names are only available for {MAX_PLOTS_FOR_CUSTOM_NAMES} plots or fewer.
                      Your current count ({parseInt(plotCount).toLocaleString()}) exceeds this limit.
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Custom names section */}
            {customizePlotNames && plotCount && parseInt(plotCount) > 0 && (
              <CustomPlotNamesSection
                count={parseInt(plotCount)}
                names={customPlotNames}
                onNameChange={(index, name) => {
                  const newNames = [...customPlotNames];
                  newNames[index] = name;
                  setCustomPlotNames(newNames);
                  setHasUserMadeChanges(true);
                }}
                duplicateIndices={duplicateNameIndices}
                disabled={disabled}
              />
            )}
          </div>
        )}
      </div>

      {/* Excel Sheet Selection */}
      {isExcelFileState && plotMapSource === "upload" && availableSheets.length > 1 && !disabled && (
        <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            <Label className="text-sm font-medium">Select Worksheet</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sheetSelection" className="text-sm">
              Choose which worksheet to use for plot map data
            </Label>
            <Select value={selectedSheet} onValueChange={handleSheetChange} disabled={disabled}>
              <SelectTrigger>
                <SelectValue placeholder="Select a worksheet" />
              </SelectTrigger>
              <SelectContent>
                {availableSheets.map(sheet => (
                  <SelectItem key={sheet.name} value={sheet.name}>
                    {sheet.name} ({sheet.rowCount} rows, {sheet.columnCount} columns)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Processing State */}
      {isProcessingExcel && !disabled && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-blue-700">Processing Excel file...</span>
        </div>
      )}

      {/* CSV Error Display */}
      {csvError && plotMapSource === "upload" && !disabled && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{csvError}</AlertDescription>
        </Alert>
      )}

      {/* CSV Warnings Display */}
      {csvWarnings.length > 0 && plotMapSource === "upload" && !disabled && (() => {
        // Filter out the row/column uniqueness warning since we show it more prominently inline
        const filteredWarnings = csvWarnings.filter(
          warning => !warning.includes("don't form unique combinations")
        );
        if (filteredWarnings.length === 0) return null;
        return (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <div className="font-medium">Please note:</div>
                {filteredWarnings.map((warning, index) => (
                  <div key={index} className="text-sm">{warning}</div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        );
      })()}

      {/* Column Selection UI */}
      {csvValidationResult && csvValidationResult.isValid && plotMapSource === "upload" && !disabled && (
        <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <Label className="text-sm font-medium">Successful upload!</Label>
          </div>

          <div className="text-xs text-muted-foreground">
            {csvValidationResult.rowCount} rows, {csvValidationResult.columnCount} columns
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="plotIdColumn" className="text-sm">
                Plot ID Column*{" "}
                <span className="text-xs text-muted-foreground">(required for plot identification)</span>
              </Label>
              <Select value={plotIdColumn} onValueChange={handlePlotIdColumnChange} disabled={disabled}>
                <SelectTrigger>
                  <SelectValue placeholder="Select plot ID column" />
                </SelectTrigger>
                <SelectContent>
                  {csvValidationResult.columnAnalysis?.uniqueValueColumns.length ? (
                    csvValidationResult.columnAnalysis.uniqueValueColumns.map(header => (
                      <SelectItem key={header} value={header}>{header}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-unique-columns" disabled>
                      No columns with unique values found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Row/Column Selection or Informational Text */}
            {noNumericColumnsForGrid ? (
              <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3 border border-muted">
                No numeric columns were found in your file that could be used for a grid-based
                plot layout. If you would like plots arranged in a grid, include integer columns
                in your file that designate the "row" and "column" position of each plot.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="rowColumn" className="text-sm">
                      Row <span className="text-xs text-muted-foreground">(optional)</span>
                    </Label>
                    <Select
                      value={rowColumn || "none"}
                      onValueChange={handleRowColumnChange}
                      disabled={disabled || validRowOptions.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select row column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {validRowOptions.map(header => (
                          <SelectItem key={header} value={header}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="colColumn" className="text-sm">
                      Column <span className="text-xs text-muted-foreground">(optional)</span>
                    </Label>
                    <Select
                      value={colColumn || "none"}
                      onValueChange={handleColColumnChange}
                      disabled={disabled || validColOptions.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {validColOptions.map(header => (
                          <SelectItem key={header} value={header}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {noValidRowColumnPairs && (
                  <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3 border border-muted">
                    Grid layout isn't available for this file — its numeric columns don't form unique row/column pairs. You can still proceed without a grid by leaving Row and Column set to "None".
                  </div>
                )}
              </>
            )}

            {/* Metadata Columns Display */}
            {csvValidationResult && plotIdColumn && (
              <div className="space-y-2 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Available Plot Metadata</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  The following columns will be available as plot metadata during data collection:
                </p>
                <div className="flex flex-wrap gap-2">
                  {csvValidationResult.headers
                    ?.filter(h =>
                      h !== plotIdColumn &&
                      h !== rowColumn &&
                      h !== colColumn
                    )
                    .map(header => (
                      <Badge key={header} variant="secondary" className="text-xs">
                        {header}
                      </Badge>
                    ))
                  }
                  {csvValidationResult.headers?.filter(h =>
                    h !== plotIdColumn && h !== rowColumn && h !== colColumn
                  ).length === 0 && (
                    <span className="text-xs text-muted-foreground italic">
                      No additional metadata columns found
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Data Preview */}
            {csvValidationResult.parsedData && csvValidationResult.parsedData.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Data Preview (first 3 rows)
                </Label>
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {csvValidationResult.headers?.map(header => (
                          <TableHead
                            key={header}
                            className={`text-xs ${header === plotIdColumn ? "bg-blue-50 font-semibold" : header === rowColumn || header === colColumn ? "bg-green-50 font-medium" : ""}`}
                          >
                            {header}
                            {header === plotIdColumn && <span className="text-blue-600 ml-1">*</span>}
                            {(header === rowColumn || header === colColumn) && <span className="text-green-600 ml-1">•</span>}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvValidationResult.parsedData.slice(0, 3).map((row, index) => (
                        <TableRow key={index}>
                          {csvValidationResult.headers?.map(header => (
                            <TableCell
                              key={header}
                              className={`text-xs ${header === plotIdColumn ? "bg-blue-50" : header === rowColumn || header === colColumn ? "bg-green-50" : ""}`}
                            >
                              {row[header] || "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Dialog */}
      <PlotMapHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
    </div>
  );
};

export default PlotMapSection;
